import {
  concat,
  each,
  indexOf,
  isEmpty,
  keys,
  map,
  partition,
  reduce,
  uniq,
} from "lodash";
import { AbstractDate, Period } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Timezone } from "@linkfluence/models/timezone";
import { isPinnedAuthorCard } from "../cards/is";
import { Card } from "../cards/model";
import { QuerySource } from "../queries";
import {
  isClauseFieldFilled,
  isCustomFieldsClause,
  isDateRangeClause,
  isNotClause,
  isQueryClause,
  isWorkspaceClause,
} from "./is";
import { Clause, InnerClause } from "./model";

export function getFieldFromInnerClause(clause: InnerClause): string {
  if (isCustomFieldsClause(clause)) {
    return keys(clause.customFields)[0];
  } else if (isNotClause(clause)) {
    return getFieldFromInnerClause(clause.not);
  } else {
    return keys(clause)[0];
  }
}

export function isInnerClauseFromField(
  innerClause: InnerClause,
  field: string
) {
  return getFieldFromInnerClause(innerClause) === field;
}

export function getFieldsFromClause(clause: Clause) {
  return uniq(
    map(clause ? clause.and : [], getFieldFromInnerClause)
  ) as Facet[];
}

export function getUsedFieldsFromClause(clause: Clause) {
  const allFields = getFieldsFromClause(clause);
  return uniq(
    allFields.map((field: string) => {
      if (field === "inRelationship" || field === "hasChildren") {
        return Facet.MaritalStatus;
      }
      return field;
    })
  );
}

export function getDateFromClause(clause: Clause) {
  let from: AbstractDate = "now";
  let to: AbstractDate = "now";
  let tz: Timezone;
  if (clause) {
    each(clause.and, (innerClause) => {
      if (isDateRangeClause(innerClause)) {
        if (innerClause.date) {
          from =
            innerClause.date.from ||
            innerClause.date.gt ||
            innerClause.date.after;
          to =
            innerClause.date.to ||
            innerClause.date.lt ||
            innerClause.date.before;
          tz = innerClause?.date?.tz;
        }
      }
    });
  }
  return { from, to, ...(tz !== undefined ? { tz } : {}) } as Period;
}

export function getWorkspaceIdCtxFromClause(clause: Clause) {
  if (!clause) {
    return null;
  }
  let workspaceId: number = null;
  each(clause.and, (innerClause) => {
    if (isWorkspaceClause(innerClause)) {
      workspaceId = innerClause.dashboard;
    }
  });
  return workspaceId;
}

export function getQueryFromClause(clause: Clause) {
  if (!clause) {
    return "";
  }
  let query: string = "";
  each(clause.and, (innerClause) => {
    if (isQueryClause(innerClause)) {
      query = innerClause.query;
    }
  });
  return query;
}

export function getClauseFromCard(card: Card) {
  if (!card || card?.fragments.length === 0 || isPinnedAuthorCard(card)) {
    return { and: [] };
  }
  return card.fragments[0].request.query;
}

export function getEmptyClause() {
  return { and: [] };
}

export function getSourcesFromClause(clause: Clause): QuerySource[] {
  const formatFocuses = (focuses: number[], status: boolean) =>
    focuses.map((focus) => ({ id: focus, include: status }));
  return clause.and.reduce((acc, innerClause) => {
    const isFocusField = getFieldFromInnerClause(innerClause) === Facet.Focuses;
    if (!isFocusField) return acc;
    if (isNotClause(innerClause)) {
      const excludedSources = formatFocuses(
        innerClause.not[Facet.Focuses],
        false
      );
      acc.push(...excludedSources);
      return acc;
    }
    const includedSources = formatFocuses(innerClause[Facet.Focuses], true);
    acc.push(...includedSources);
    return acc;
  }, []);
}

export function getClauseFromSources(sources: QuerySource[]): Clause {
  const innerClause: InnerClause[] = [];
  const [included, excluded] = partition(sources, { include: true });

  if (!isEmpty(included)) {
    innerClause.push({ [Facet.Focuses]: map(included, "id") });
  }
  if (!isEmpty(excluded)) {
    innerClause.push({
      not: { [Facet.Focuses]: map(excluded, "id") },
    });
  }

  return { and: innerClause };
}

export const getInnerClauseFromClause = (
  clause: Clause,
  field: Facet
): { [key: string]: any } => {
  if (!clause || !clause.and) {
    return undefined;
  }
  const result = {};
  clause.and.forEach((innerClause) => {
    if (getFieldFromInnerClause(innerClause) === field) {
      if (isCustomFieldsClause(innerClause)) {
        result[field] = innerClause.customFields[field];
      } else if (isNotClause(innerClause)) {
        result["not"] = innerClause.not;
      } else {
        result[field] = innerClause[field];
      }
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

export const getFieldClauseFromClause = (
  clause: Clause,
  field: Facet
): Clause => {
  const baseClause = { and: [] };
  const innerClause = getInnerClauseFromClause(clause, field);
  if (!innerClause) {
    return baseClause;
  }
  if (innerClause.not) {
    baseClause.and.push({ not: innerClause.not });
  }
  baseClause.and.push({ [field]: innerClause[field] });
  return baseClause;
};

//disclaimer : only works for standard string valued fields for now (ex: platforms).
//We check if target field has a filter or not. If it does, we check if target value is included
export const isFieldValueIncluded = (
  clause: Clause,
  field: Facet,
  value: any
): boolean => {
  const isFilled = isClauseFieldFilled(clause, field);
  if (!isFilled) {
    return true;
  } else {
    const innerClause = getInnerClauseFromClause(clause, field);
    return (
      (!innerClause.not || indexOf(innerClause["not"][field], value) === -1) &&
      (!innerClause[field] || indexOf(innerClause[field], value) !== -1)
    );
  }
};

const flattenClauses = (clause: Clause, acc = []) =>
  reduce(
    clause.and,
    (acc, subClause) => {
      //@ts-ignore : subClause has possibly no and property (if it is an innerclause);
      acc = subClause.and
        ? flattenClauses(subClause as Clause, acc)
        : concat(acc, subClause);
      return acc;
    },
    acc
  );

export function flattenInnerClauses(clause: Clause, acc = {}): Clause {
  return { ...clause, and: flattenClauses(clause) };
}
